<template>
  <div class="flex flex-col flex-1 overflow-x-hidden">
    <FlotoCrudContainer
      ref="list"
      :scrollable="false"
      :fetch-fn="fetchData"
      :delete-fn="deleteData"
      :create-fn="createData"
      :update-fn="updateData"
      :show-no-data="false"
      :as-table="asTable"
      :columns="tableColumns"
    >
      <template v-slot:add-controls="{ create, refreshList, items }">
        <MRow
          v-if="enableCreateButton(items) || searchable"
          class="mb-4"
          :gutter="0"
        >
          <MCol :size="6">
            <MInput
              v-if="searchable"
              v-model="searchTerm"
              class="search-box"
              :placeholder="$t('search')"
              @update="refreshList"
            >
              <template v-slot:prefix>
                <MIcon name="search" />
              </template>
            </MInput>
          </MCol>
          <MCol
            v-if="enableCreateButton(items) && canEdit"
            :size="6"
            class="flex justify-end"
          >
            <MButton variant="primary" @click="handleCreate(create)">
              {{ $t('add') }} {{ __tc(type) }}
            </MButton>
          </MCol>
        </MRow>
        <MRow v-if="type === 'ram_component'" class="my-5 items-center">
          <MCol :size="2" style="padding-right: 1px">
            <BoxNode
              :title="`${$tc('total')} ${__tc('slot', 2)}`"
              :shadow="false"
              :count="ramSlotData.totalSlots"
            />
          </MCol>
          <MCol style="padding-right: 1px" :size="2">
            <BoxNode
              :title="`${__tc('free')} ${__tc('slot', 2)}`"
              :shadow="false"
              :count="ramSlotData.totalFree"
            />
          </MCol>
          <MCol style="padding-right: 1px" :size="2">
            <BoxNode
              :title="`${__tc('occupied')} ${__tc('slot', 2)}`"
              :shadow="false"
              :count="ramSlotData.totalOccupied"
            />
          </MCol>
          <MCol style="padding-right: 1px" :size="2">
            <BoxNode
              :title="`${$tc('total')} ${$tc('asset_enum.ram')} ${$tc(
                'size'
              )} (${$tc('gb')})`"
              :shadow="false"
            >
              <template v-slot:box-count>
                <span v-if="ramSlotData.totalSize !== 0">
                  <b>{{ ramSlotData.totalSize | bytes('gb', false, false) }}</b>
                </span>
                <span v-else>
                  <b>{{ ramSlotData.totalSize }}</b>
                </span>
              </template>
            </BoxNode>
          </MCol>
          <MDivider class="mb-0" />
        </MRow>
      </template>
      <template v-slot:form-header="{ item }">
        {{ $t(item.id ? 'edit' : 'add') }} {{ __tc(type) }}
      </template>
      <template v-slot:form-items="{ item: data }">
        <PropertyComponentForm
          :field-details="fieldDetails"
          :type="type"
          :form-data="data"
          :custom-fields="availableCustomFieldForAssetType"
          :module-name="moduleName"
        />
      </template>
      <template v-slot:form-actions="{ submit, cancel, item, processing }">
        <MButton
          id="add-update-btn"
          class="mx-1"
          :loading="processing"
          @click="submit"
        >
          {{ $t(item.id ? 'update' : 'add') }}
        </MButton>
        <MButton id="cancel-btn" variant="default" @click="cancel">
          {{ $t('cancel') }}
        </MButton>
      </template>
      <template v-slot:softwareName="{ item }">
        <td class="text-ellipsis">
          <component
            :is="canCreate ? 'a' : 'span'"
            :href="
              $router.resolve(
                $modules.getModuleRoute('asset', 'view', {
                  params: {
                    id: item.softwareAssetId,
                    assetType: $constants.ASSET_SOFTWARE,
                  },
                })
              ).href
            "
            target="_blank"
          >
            {{ item.softwareName }}
          </component>
        </td>
      </template>
      <template v-slot:appName="{ item }">
        <td class="text-ellipsis">
          <a
            :href="
              $router.resolve(
                $modules.getModuleRoute('asset', 'view', {
                  params: {
                    id: item.softwareAssetId,
                    assetType: $constants.ASSET_SOFTWARE,
                  },
                })
              ).href
            "
            target="_blank"
          >
            {{ item.appName }}
          </a>
        </td>
      </template>
      <template v-slot:name="{ item }">
        <td class="text-ellipsis">
          <a
            :href="
              $router.resolve(
                moduleName === $constants.CMDB
                  ? $modules.getModuleRoute('cmdb', 'view', {
                      params: {
                        id: item.id,
                        ciType: $constants.CMDB,
                      },
                    })
                  : $modules.getModuleRoute('asset', 'view', {
                      params: {
                        id: item.id,
                        assetType: $constants.ASSET_HARDWARE,
                      },
                    })
              ).href
            "
            target="_blank"
          >
            {{ moduleName === $constants.CMDB ? item.ciName : item.name }}
          </a>
        </td>
      </template>
      <template v-slot:manufacturerId="{ item }">
        <td class="text-ellipsis">
          <ManufacturerPicker
            v-if="item.manufacturerId"
            :key="item.manufacturerId"
            v-model="item.manufacturerId"
            as-input
            disabled
            text-only
          />
          <span v-else></span>
        </td>
      </template>
      <template v-slot:accountType="{ item }">
        <td class="text-ellipsis">
          {{
            item.accountType
              ? (
                  userAccountTypeOptions.find(
                    (i) => i.key === item.accountType
                  ) || {}
                ).text
              : ''
          }}
        </td>
      </template>
      <template v-slot:licenseType="{ item }">
        <td class="text-ellipsis">
          {{
            item.licenseType
              ? (
                  activationStatusOptions.find(
                    (i) => i.key === item.licenseType
                  ) || {}
                ).text
              : ''
          }}
        </td>
      </template>
      <template v-slot:osCompatibility="{ item }">
        <td class="text-ellipsis">
          {{
            item.osCompatibility
              ? (
                  osArchitectureOptions.find(
                    (i) => i.key === item.osCompatibility
                  ) || {}
                ).text
              : ''
          }}
        </td>
      </template>
      <template v-slot:disabled="{ item }">
        <td class="text-ellipsis">
          {{ $tc(`asset_enum.${String(item.disabled)}`) }}
        </td>
      </template>
      <template v-slot:installDate="{ item }">
        <td class="text-ellipsis" :title="item.installDate | datetime">
          {{ item.installDate | datetime }}
        </td>
      </template>
      <template v-slot:installedsize="{ item }">
        <td class="text-ellipsis">
          <template v-if="item.installedsize">
            {{
              convertValueForDescription('bytes', item.installedsize, {
                unit: 'mb',
              })
            }}
          </template>
          <template v-else> </template>
        </td>
      </template>
      <template
        v-for="column in customFieldColumns"
        v-slot:[column.key]="{ item }"
      >
        <td :key="column.key" class="text-ellipsis">
          <CustomAsSystemPicker
            v-if="(column || {}).fieldResolutionType === 'system_field'"
            disabled
            text-only
            :as-input="false"
            :value="(item.fieldValueDetails || {})[column.key]"
            :module-name="moduleName"
            placeholder="---"
            :field-reference-type="(column || {}).fieldReferenceType"
          />
          <span v-else>
            {{ getCustomFieldValue(column, item) }}
          </span>
        </td>
      </template>
      <template v-slot:actions="{ edit, remove, item }">
        <td>
          <MTooltip v-if="enableEditButton">
            <template v-slot:trigger>
              <MButton
                variant="transparent"
                size="small"
                :shadow="false"
                shape="circle"
                @click="edit(item)"
              >
                <MIcon name="pencil" class="cursor-pointer text-primary" />
              </MButton>
            </template>
            {{ $t('edit') }}
          </MTooltip>
          <FlotoDeleteBtn
            v-if="canDelete"
            :message="$t('confirm_remove_item', { item: __tc(type) })"
            class="ml-2"
            @confirm="remove"
          />
        </td>
      </template>
      <template v-slot:item="{ item, isLastItem, edit, update, remove }">
        <PropertyComponentItem
          :type="type"
          :field-details="fieldDetails"
          :item="item"
          :can-edit="enableEditButton"
          :can-delete="canDelete"
          :custom-fields="availableCustomFieldForAssetType"
          :module-name="moduleName"
          @edit="edit"
          @remove="remove"
          @update="update"
        />
        <MDivider v-if="!isLastItem" />
      </template>

      <template v-slot:memorySize="{ item }">
        <td class="text-ellipsis">
          {{
            convertValueForDescription(
              'bytes',
              (item.NutanixVmPropertyGroup || []).memorySize ||
                (item.NutanixHostPropertyGroup || []).memorySize,
              {
                unit: 'gb',
              }
            )
          }}
        </td>
      </template>
      <template v-slot:UUID="{ item }">
        <td class="text-ellipsis">
          {{
            (item.NutanixVmPropertyGroup || []).UUID ||
            (item.NutanixClusterPropertyGroup || []).UUID ||
            '---'
          }}
        </td>
      </template>
      <template v-slot:powerState="{ item }">
        <td class="text-ellipsis">
          {{ (item.NutanixVmPropertyGroup || []).powerState || '---' }}
        </td>
      </template>
      <template v-slot:hyperVType="{ item }">
        <td class="text-ellipsis">
          {{ (item.NutanixVmPropertyGroup || []).hyperVType || '---' }}
        </td>
      </template>
      <template v-slot:hostUUID="{ item }">
        <td class="text-ellipsis">
          {{ (item.NutanixVmPropertyGroup || []).hostUUID || '---' }}
        </td>
      </template>

      <template v-slot:poolId="{ item }">
        <td class="text-ellipsis">
          {{ (item.NutanixStoragePoolPropertyGroup || []).poolId || '---' }}
        </td>
      </template>
      <template v-slot:uuid="{ item }">
        <td class="text-ellipsis">
          {{
            (item.NutanixStoragePoolPropertyGroup || []).uuid ||
            (item.NutanixStorageContainerPropertyGroup || []).uuid ||
            '---'
          }}
        </td>
      </template>
      <template v-slot:size="{ item }">
        <td class="text-ellipsis">
          {{
            convertValueForDescription(
              'bytes',
              (item.NutanixStoragePoolPropertyGroup || []).size ||
                (item.NutanixStorageContainerPropertyGroup || []).size,
              {
                unit: 'gb',
              }
            )
          }}
        </td>
      </template>

      <template v-slot:containerId="{ item }">
        <td class="text-ellipsis">
          {{
            (item.NutanixStorageContainerPropertyGroup || []).containerId ||
            '---'
          }}
        </td>
      </template>

      <template v-slot:state="{ item }">
        <td class="text-ellipsis">
          {{
            (item.NutanixHostPropertyGroup || []).state ||
            (item.NutanixClusterPropertyGroup || []).state ||
            '---'
          }}
        </td>
      </template>
      <template v-slot:numberOfVms="{ item }">
        <td class="text-ellipsis">
          {{ (item.NutanixHostPropertyGroup || []).numberOfVms || '---' }}
        </td>
      </template>
      <template v-slot:hostType="{ item }">
        <td class="text-ellipsis">
          {{ (item.NutanixHostPropertyGroup || []).hostType || '---' }}
        </td>
      </template>
      <template v-slot:cvmIP="{ item }">
        <td class="text-ellipsis">
          {{ (item.NutanixHostPropertyGroup || []).cvmIP || '---' }}
        </td>
      </template>
      <template v-slot:cpuSpeed="{ item }">
        <td class="text-ellipsis">
          {{
            convertValueForDescription(
              'hertz',
              (item.NutanixHostPropertyGroup || []).cpuSpeed,
              {
                unit: 'ghz',
              }
            )
          }}
        </td>
      </template>

      <template v-slot:clusterName="{ item }">
        <td class="text-ellipsis">
          {{ (item.NutanixClusterPropertyGroup || []).clusterName || '---' }}
        </td>
      </template>
      <template v-slot:version="{ item }">
        <td class="text-ellipsis">
          {{ (item.NutanixClusterPropertyGroup || []).version || '---' }}
        </td>
      </template>
      <template v-slot:internalSubnet="{ item }">
        <td class="text-ellipsis">
          {{ (item.NutanixClusterPropertyGroup || []).internalSubnet || '---' }}
        </td>
      </template>
      <template v-slot:externalSubnet="{ item }">
        <td class="text-ellipsis">
          {{ (item.NutanixClusterPropertyGroup || []).externalSubnet || '---' }}
        </td>
      </template>
      <template v-slot:architecture="{ item }">
        <td class="text-ellipsis">
          {{ (item.NutanixClusterPropertyGroup || []).architecture || '---' }}
        </td>
      </template>
    </FlotoCrudContainer>
    <FlotoDrawer
      :open="showSoftwareSelectionDrawer"
      width="50%"
      @hide="hideDrawer"
    >
      <template v-slot:title>
        {{ $t('add') }}
        {{
          type === 'mobile_application_component'
            ? $tc('mobile_application_component')
            : $tc('software', 2)
        }}
      </template>
      <AssetSelectionList
        selectable
        :module-name="$constants.ASSET_SOFTWARE"
        searchable
        :fetch-fn="getSoftwareComponent"
        @selection-change="setSelectedSoftwares"
      />
      <template v-slot:actions="{ hide }">
        <MButton
          :disabled="selectedSoftwares.length === 0"
          outline
          class="mx-1"
          :loading="softwareProcessing"
          @click="handleAddSoftwares"
        >
          {{ $t('add') }} {{ $tc('software') }}
        </MButton>
        <MButton variant="default" @click="hide">
          {{ $t('cancel') }}
        </MButton>
      </template>
    </FlotoDrawer>
  </div>
</template>

<script>
import { FormComputed } from '@state/modules/form'
import { AssetTypeComputed } from '@state/modules/asset-type'
import { CITypeComputed } from '@state/modules/ci-type'
import AssetSelectionList from '@components/item-selection-list/asset-list'
import ManufacturerPicker from '@components/data-picker/manufacturer-picker'
import { convertValueForDescription } from '@utils/value'
import { flattenRecursive, findValuePathWithItems } from '@data/recursive'
import CustomAsSystemPicker from '@modules/form/components/fields/custom-as-system.vue'
import BoxNode from '@modules/asset-management/components/discovery-component/discovery-service/box-node'
import { customFieldAttributesTypePrefix } from '@modules/asset/helpers/asset-custom-fields'
import {
  MOBILE_APPLICATION_COMPONENT,
  propertyComponentFieldMap,
  SOFTWARE_COMPONENT,
} from '../../helpers/asset'
import {
  userAccountTypeOptions,
  activationStatusOptions,
  osArchitectureOptions,
} from '../../helpers/option-map'
import {
  searchSoftwareComponentApi,
  searchAssetPropertiesApi,
} from '../../asset-api'
import PropertyComponentForm from './property-component-form'
import PropertyComponentItem from './property-component-list-item'
export default {
  name: 'ListContainer',
  components: {
    PropertyComponentForm,
    PropertyComponentItem,
    ManufacturerPicker,
    AssetSelectionList,
    BoxNode,
    CustomAsSystemPicker,
  },
  props: {
    moduleName: { type: String, required: true },
    resourceId: { type: Number, required: true },
    resource: { type: Object, required: true },
    fetchFn: { type: Function, default: undefined },
    deleteFn: { type: Function, default: undefined },
    createFn: { type: Function, default: undefined },
    updateFn: { type: Function, default: undefined },
    searchable: { type: Boolean, default: false },
    type: { type: String, required: true },
    canCreate: { type: Boolean, default: false },
    canEdit: { type: Boolean, default: false },
    canDelete: { type: Boolean, default: false },
    asTable: { type: Boolean, default: false },
    // eslint-disable-next-line
    hasActions: { type: Boolean, default: true },
  },
  data() {
    this.userAccountTypeOptions = userAccountTypeOptions()
    this.activationStatusOptions = activationStatusOptions()
    this.osArchitectureOptions = osArchitectureOptions()
    this.convertValueForDescription = convertValueForDescription
    this.propertyComponentFieldMap = propertyComponentFieldMap()
    return {
      searchTerm: '',
      showSoftwareSelectionDrawer: false,
      selectedSoftwares: [],
      softwareProcessing: false,
      ramSlotData: {},
    }
  },
  computed: {
    ...FormComputed,
    ...AssetTypeComputed,
    ...CITypeComputed,
    parentModuleName() {
      if (this.moduleName === this.$constants.CMDB) {
        return this.$constants.CMDB
      }
      return this.$constants.ASSET
    },
    enableEditButton() {
      if (
        'canEdit' in this.fieldDetails &&
        this.fieldDetails.canEdit === false
      ) {
        return false
      }
      return this.canEdit
    },
    customFields() {
      const typePrefix = customFieldAttributesTypePrefix(this.parentModuleName)
      return (
        ((this.assetForm || {}).fields || []).filter(
          (f) =>
            f.attributes.model === this.parentModuleName &&
            (f.attributes[`${typePrefix}PropertyType`] === this.type ||
              f.attributes[`${typePrefix}ComponentType`] === this.type)
        ) || []
      )
    },
    selectedAssetTypes() {
      const typePrefix = customFieldAttributesTypePrefix(this.parentModuleName)
      if (this.resource[`${typePrefix}TypeId`]) {
        const collection = this[`${typePrefix}Types`]
        const assetTypeId = this.resource[`${typePrefix}TypeId`]
        const path = findValuePathWithItems(collection, assetTypeId)
        const rootItem = path[0]
        return {
          list: path,
          ...rootItem,
        }
      }
      return {}
    },
    availableCustomFieldForAssetType() {
      const typePrefix = customFieldAttributesTypePrefix(this.parentModuleName)
      if (!this.resource[`${typePrefix}TypeId`]) {
        return []
      }
      const fullPath = this.selectedAssetTypes.list
      let assetTypeFields = []
      fullPath.forEach((p) => {
        const c = this.customFields.filter(
          (f) => f.attributes[`${typePrefix}TypeId`] === String(p.id)
        )
        assetTypeFields = assetTypeFields.concat(c)
      })
      return assetTypeFields || []
    },
    fieldDetails() {
      return this.propertyComponentFieldMap[this.type]
    },
    customFieldColumns() {
      const customColumns = this.availableCustomFieldForAssetType.map((c) => ({
        name: c.title,
        key: `${c.id}`,
        fieldType: c.inputType,
        attributes: c.attributes,
        ...(c.inputType === 'dependent' ? { tree: c.tree } : {}),
        fieldReferenceType: c.fieldReferenceType,
        fieldResolutionType: c.fieldResolutionType,
      }))
      return customColumns
    },
    tableColumns() {
      const fields = this.fieldDetails.fields.map((field) => {
        return {
          name: field.name,
          key: field.paramName,
        }
      })
      return [
        ...fields,
        ...this.customFieldColumns,
        ...(this.hasActions
          ? [{ name: this.$tc('action', 2), key: 'actions', width: '120px' }]
          : []),
      ]
    },
  },
  created() {
    if (this.type === 'ram_component') {
      this.getAllcoatedSlotData()
    }
  },
  methods: {
    getAllcoatedSlotData() {
      return searchAssetPropertiesApi(
        this.moduleName,
        this.resourceId,
        'ram_property_group'
      ).then((data) => {
        this.ramSlotData = data.items[0]
      })
    },
    enableCreateButton(items) {
      if (
        this.canCreate &&
        !this.fieldDetails.maxCreation &&
        this.fieldDetails.canCreate
      ) {
        return true
      } else if (
        this.canCreate &&
        this.fieldDetails.canCreate &&
        this.fieldDetails.maxCreation &&
        (!items.length || this.fieldDetails.maxCreation > items.length)
      ) {
        return true
      } else {
        return false
      }
    },
    handleCreate(createCallBack) {
      if (
        [SOFTWARE_COMPONENT, MOBILE_APPLICATION_COMPONENT].indexOf(this.type) >=
        0
      ) {
        return (this.showSoftwareSelectionDrawer = true)
      }
      return createCallBack()
    },
    handleAddSoftwares() {
      return this.createData(this.selectedSoftwares.map((i) => i.id))
        .then(() => this.refresh())
        .finally(() => {
          this.hideDrawer()
        })
    },
    setSelectedSoftwares(softwares) {
      this.selectedSoftwares = softwares
    },
    hideDrawer() {
      this.selectedSoftwares = []
      this.showSoftwareSelectionDrawer = false
    },
    fetchData(limit, offset) {
      if (this.fetchFn) {
        return this.fetchFn({ name: this.searchTerm }, limit, offset)
      } else {
        throw new Error('fetch function is missing')
      }
    },
    deleteData({ id }) {
      if (this.deleteFn) {
        return this.deleteFn(id).then((response) => {
          if (this.type === 'ram_component') {
            this.getAllcoatedSlotData()
          }
          return response
        })
      } else {
        if (this.canDelete) {
          throw new Error('delete function is missing')
        }
      }
    },
    updateData(data) {
      if (this.updateFn) {
        return this.updateFn(data).then((response) => {
          if (this.type === 'ram_component') {
            this.getAllcoatedSlotData()
          }
          return response
        })
      } else {
        if (this.canEdit) {
          throw new Error('update function is missing')
        }
      }
    },
    createData(data) {
      if (this.createFn) {
        return this.createFn(data).then((response) => {
          if (this.type === 'ram_component') {
            this.getAllcoatedSlotData()
          }
          return response
        })
      } else {
        if (this.canCreate) {
          throw new Error('create function is missing')
        }
      }
    },
    getCustomFieldValue(column, item) {
      if (column.fieldType === 'dependent') {
        return this.getDependentFieldValue(column, item)
      }
      const value = convertValueForDescription(
        column.fieldType,
        (item.fieldValueDetails || {})[column.key],
        { attributes: column.attributes }
      )
      if (value) {
        return value
      }
      return '---'
    },
    getDependentFieldValue(column, item) {
      const flattenedTree = flattenRecursive(column.tree)
      return (
        (
          flattenedTree.find(
            (o) => o.id === (item.fieldValueDetails || {})[column.key]
          ) || {}
        ).name || '---'
      )
    },
    refresh() {
      this.$refs.list.refresh()
    },
    getSoftwareComponent(_, criterias, limit, offset) {
      return searchSoftwareComponentApi(
        this.resourceId,
        criterias,
        limit,
        offset
      )
    },
  },
}
</script>
